import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

interface PersistedSearchParamsOptions {
    storageKey: string;
    keys: string[];
}

const usePersistedSearchParams = ({ storageKey, keys }: PersistedSearchParamsOptions) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const restoredRef = useRef(false);

    const persistedParams = new URLSearchParams([...searchParams].filter(([key]) => keys.includes(key))).toString();
    const isRestoring = !persistedParams && !restoredRef.current && Boolean(localStorage.getItem(storageKey));

    useEffect(() => {
        const storedParams = restoredRef.current ? '' : localStorage.getItem(storageKey) || '';
        restoredRef.current = true;

        if (persistedParams) {
            localStorage.setItem(storageKey, persistedParams);
        } else if (storedParams) {
            setSearchParams(
                (params) => {
                    new URLSearchParams(storedParams).forEach((value, key) => params.set(key, value));
                    return params;
                },
                { replace: true }
            );
        } else {
            // when the search params are actively removed via "clear all" button
            localStorage.removeItem(storageKey);
        }
    }, [persistedParams, storageKey, setSearchParams]);

    return isRestoring;
};

export default usePersistedSearchParams;
